import { Link, useRouterState } from '@tanstack/react-router'
import { Eye, Lock, Moon, Search, Sun } from 'lucide-react'
import type { Me } from '@nw/shared/api/auth'
import { useLocale } from '../../lib/i18n.tsx'
import { useTheme } from '../../lib/theme.ts'
import { Button } from '../../kit/Button.tsx'
import { Kbd } from '../../kit/Kbd.tsx'
import { useCountdown } from '../../kit/StepUpSheet.tsx'
import { Tooltip } from '../../kit/Tooltip.tsx'
import { entryFor } from '../nav.ts'
import { openOverlay } from '../ui.ts'
import { BrandMark } from './Brand.tsx'
import { EnvBadge } from './EnvBadge.tsx'
import { UserMenu } from './UserMenu.tsx'

/**
 * The owner's register state from /api/me: names masked, or visible with the step-up countdown. Display only: the
 * step-up itself is the P1 auth piece's (StepUpSheet), and the server masks every name until then.
 */
function RegisterPill({ me }: { me: Me }) {
  const { t } = useLocale()
  const until = me.stepUpUntil ? Date.parse(me.stepUpUntil) : undefined
  const { left, text } = useCountdown(until)
  if (me.role !== 'owner' || me.via !== 'session') return null
  return left > 0 ? (
    <Tooltip content={t('common.register.visibleHint')}>
      <span
        tabIndex={0}
        className="inline-flex h-7 items-center gap-1.5 rounded-full bg-ok-soft px-2.5 text-xs font-medium text-ok ring-1 ring-ok-line ring-inset"
      >
        <Eye size={13} strokeWidth={2} aria-hidden />
        <span className="hidden lg:inline">{t('common.register.visible')}</span>
        <span className="font-mono tabular" role="timer">
          {text}
        </span>
      </span>
    </Tooltip>
  ) : (
    <Tooltip content={t('common.register.maskedHint')}>
      <span
        tabIndex={0}
        className="hidden h-7 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium text-ink-muted ring-1 ring-line-strong ring-inset lg:inline-flex"
      >
        <Lock size={12} strokeWidth={2} aria-hidden />
        {t('common.register.masked')}
      </span>
    </Tooltip>
  )
}

function Crumb({ pathname }: { pathname: string }) {
  const { t } = useLocale()
  const entry = entryFor(pathname)
  const Icon = entry?.icon
  return (
    <div className="flex min-w-0 items-center gap-1.5 text-sm">
      {Icon && <Icon size={15} strokeWidth={1.75} aria-hidden className="shrink-0 text-ink-faint" />}
      {entry ? (
        <Link to={entry.to} className="truncate rounded-xs font-medium text-ink transition hover:text-ink-muted">
          {t(entry.label)}
        </Link>
      ) : (
        <span className="truncate font-medium text-ink">{t('common.appName')}</span>
      )}
    </div>
  )
}

/** Language switch (decision B20): nb by default, en on request; kept on this device. */
export function LanguageSwitch({ className }: { className?: string }) {
  const { t, locale, setLocale } = useLocale()
  const other = locale === 'nb' ? 'en' : 'nb'
  const label = t('common.language.switch', { language: t(`common.language.${locale}`), other: t(`common.language.${other}`) })
  return (
    <Tooltip content={label}>
      <button
        type="button"
        onClick={() => setLocale(other)}
        aria-label={label}
        className={
          'h-7 items-center rounded-sm px-1.5 font-mono text-xs font-medium tracking-label text-ink-muted uppercase ring-1 ring-line-strong transition ring-inset hover:bg-hover hover:text-ink ' +
          (className ?? 'inline-flex')
        }
      >
        {locale}
      </button>
    </Tooltip>
  )
}

/** Top bar: breadcrumb, omnibar trigger with ⌘K, register state, language, theme and the account menu. */
export function TopBar({ me, fixture }: { me: Me | null; fixture?: boolean }) {
  const { t } = useLocale()
  const [theme, setTheme] = useTheme()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const entry = entryFor(pathname)
  const ThemeIcon = theme === 'dark' ? Sun : Moon
  const themeLabel = theme === 'dark' ? t('common.theme.toLight') : t('common.theme.toDark')

  return (
    <header className="sticky top-0 z-30 shrink-0 border-b border-line bg-glass pt-safe backdrop-blur-md md:static md:bg-transparent md:pt-0 md:backdrop-blur-none">
      <div className="flex h-topbar items-center gap-3 px-3 md:px-4">
        {/* Phone: brand and screen title */}
        <div className="flex min-w-0 flex-1 items-center gap-2.5 md:hidden">
          <BrandMark />
          <span className="truncate font-display text-lg font-semibold text-ink">{entry ? t(entry.label) : t('common.appName')}</span>
        </div>

        {/* Desktop: breadcrumb */}
        <div className="hidden w-52 min-w-0 shrink-0 md:flex lg:w-64">
          <Crumb pathname={pathname} />
        </div>

        <div className="hidden min-w-0 flex-1 justify-center md:flex">
          {me && (
            <button
              type="button"
              onClick={() => openOverlay('omnibar')}
              aria-keyshortcuts="Meta+K Control+K"
              className="group flex h-9 w-full max-w-md items-center gap-2.5 rounded-lg bg-sunken px-3 text-sm text-ink-faint ring-1 ring-line-strong transition ring-inset hover:text-ink-muted hover:ring-accent-line"
            >
              <Search size={15} strokeWidth={1.75} aria-hidden />
              <span className="flex-1 truncate text-left">{t('common.search.trigger')}</span>
              <Kbd keys={['⌘', 'K']} />
            </button>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <EnvBadge fixture={fixture} />
          {me && <RegisterPill me={me} />}
          {me && (
            <span className="md:hidden">
              <Button size="icon" variant="ghost" icon={Search} aria-label={t('common.search.open')} onClick={() => openOverlay('omnibar')} />
            </span>
          )}
          <LanguageSwitch className="hidden sm:inline-flex" />
          <span className="hidden sm:inline-flex">
            <Tooltip content={themeLabel}>
              <Button size="icon" variant="ghost" icon={ThemeIcon} aria-label={themeLabel} onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} />
            </Tooltip>
          </span>
          {me && <UserMenu me={me} />}
        </div>
      </div>
    </header>
  )
}
